import { useState, useRef } from "react";
import useFocoModal from "../../hooks/useFocoModal";
import Icon from "../Icon";
import { BtnPrimary } from "../Atoms";
import { fmt } from "../../utils";
import Campo from "../forms/Campo";

const SALTO = String.fromCharCode(10);

const lbS  = {fontSize:10,color:"var(--text3)",fontWeight:700,display:"block",marginBottom:5,textTransform:"uppercase",letterSpacing:".06em"};
const inS  = {width:"100%",padding:"9px 11px",borderRadius:8,border:"1px solid var(--border)",fontSize:13,fontFamily:"var(--font)",boxSizing:"border-box",background:"var(--surface2)",color:"var(--text)"};

// Importador de cuadrantes pegados desde Excel, en dos pasos:
//   1. se analiza el texto sin escribir nada
//   2. si hay nombres que el sistema no reconoce ("toni", "Amoreno"), se pide a
//      quién corresponden y solo entonces se confirma
// Sirve igual para guardias y para vacaciones: cambia el texto de ayuda y el
// resumen, no el flujo.
export default function ImportarCuadranteModal({
  titulo, ayuda, ejemplo = [], users = [],
  onComprobar,          // (text, mapa) → { sin_reconocer, errores, vista_previa, ... }
  onConfirmar,          // (text, mapa) → resultado final
  resumenPrevia,        // (r) → string
  onClose,
}) {
  const cajaRef = useRef(null);
  useFocoModal(cajaRef, true, onClose);
  const [text,  setText ] = useState("");
  const [prev,  setPrev ] = useState(null);   // resultado del análisis
  const [mapa,  setMapa ] = useState({});     // nombre del Excel → id de usuario
  const [busy,  setBusy ] = useState(false);

  const lineas = text.split("\n").filter(l => l.trim()).length;
  const pendientes = (prev?.sin_reconocer || []).filter(n => !mapa[n]);
  const listo = prev && !pendientes.length;

  const analizar = async () => {
    if (!lineas || busy) return;
    setBusy(true);
    try { setPrev(await onComprobar(text, mapa)); }
    finally { setBusy(false); }
  };

  const confirmar = async () => {
    if (!listo || busy) return;
    setBusy(true);
    try { await onConfirmar(text, mapa); }
    finally { setBusy(false); }
  };

  const usuariosOrdenados = [...users].sort((a,b)=>(a.name||"").localeCompare(b.name||""));

  return (
    <div className="modal-bg"
      style={{position:"fixed",inset:0,background:"rgba(0,0,0,.38)",backdropFilter:"blur(3px)",zIndex:160,display:"flex",alignItems:"center",justifyContent:"center",padding:20}}
      onClick={onClose}>
      <div ref={cajaRef} role="dialog" aria-modal="true" className="modal-box"
        style={{background:"var(--surface)",borderRadius:16,boxShadow:"var(--shadow-lg)",width:"100%",maxWidth:580,padding:24,maxHeight:"90vh",overflowY:"auto"}}
        onClick={e=>e.stopPropagation()}>

        <h3 style={{margin:"0 0 4px",fontWeight:700,fontSize:16,display:"flex",alignItems:"center",gap:8}}>
          <Icon name="download" size={15} color="var(--accent)"/> {titulo}
        </h3>
        <p style={{margin:"0 0 14px",fontSize:11.5,color:"var(--text3)",lineHeight:1.55}}>{ayuda}</p>

        <Campo><label style={lbS}>Pega aquí las filas</label>
        <textarea value={text} onChange={e=>{setText(e.target.value);setPrev(null);}}
          placeholder={Array.isArray(ejemplo) ? ejemplo.join(SALTO) : ejemplo}
          style={{...inS,minHeight:150,resize:"vertical",fontFamily:"var(--mono)",fontSize:11.5}}/></Campo>

        <div style={{display:"flex",alignItems:"center",gap:8,margin:"10px 0 4px"}}>
          <span style={{flex:1,fontSize:11,color:"var(--text3)"}}>
            {lineas} línea{lineas===1?"":"s"}
            {prev && ` · ${resumenPrevia?.(prev) || ""}`}
          </span>
          <button className="btn" onClick={analizar} disabled={!lineas || busy}
            style={{padding:"7px 14px",borderRadius:9,border:"1px solid var(--border)",cursor:lineas?"pointer":"default",
              fontSize:12.5,background:"var(--surface2)",color:"var(--text2)",fontFamily:"var(--font)",opacity:lineas?1:.5}}>
            {busy && !prev ? "Analizando…" : "Analizar"}
          </button>
        </div>

        {/* ── Nombres que hay que emparejar ── */}
        {prev?.sin_reconocer?.length > 0 && (
          <div style={{marginTop:14,padding:14,borderRadius:11,background:"var(--warn-bg)",border:"1px solid var(--warn-line)"}}>
            <div style={{display:"flex",alignItems:"center",gap:7,fontSize:12,fontWeight:700,color:"var(--warn-text)",marginBottom:4}}>
              <Icon name="exclamation-triangle-fill" size={12} color="#a16207"/>
              {prev.sin_reconocer.length} nombre{prev.sin_reconocer.length===1?"":"s"} sin identificar
            </div>
            <p style={{fontSize:11,color:"var(--warn-text)",lineHeight:1.5,margin:"0 0 10px"}}>
              No se importa nada hasta que todos estén emparejados: un cuadrante a medias
              parece completo y no lo está.
            </p>
            {prev.sin_reconocer.map(n => (
              <div key={n} style={{display:"flex",alignItems:"center",gap:8,marginBottom:6}}>
                <span style={{width:110,flexShrink:0,fontSize:12,fontWeight:700,color:"var(--text)",fontFamily:"var(--mono)"}}>{n}</span>
                <Icon name="chevron-right" size={11} color="var(--text3)"/>
                <select aria-label={"Persona para " + n} value={mapa[n] || ""} onChange={e=>setMapa(p=>({...p,[n]:e.target.value}))}
                  style={{...inS,flex:1,padding:"6px 9px",fontSize:12,background:"var(--surface)"}}>
                  <option value="">— Elige a quién corresponde —</option>
                  {usuariosOrdenados.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                </select>
              </div>
            ))}
            <button className="btn" onClick={analizar} disabled={pendientes.length > 0 || busy}
              style={{marginTop:6,padding:"6px 12px",borderRadius:8,border:"1px solid var(--warn-line)",
                background:pendientes.length?"transparent":"var(--surface)",cursor:pendientes.length?"default":"pointer",
                fontSize:11.5,color:"var(--warn-text)",fontFamily:"var(--font)",opacity:pendientes.length?.5:1}}>
              Volver a analizar con estos nombres
            </button>
          </div>
        )}

        {/* ── Líneas que no se han podido leer ── */}
        {prev?.errores?.length > 0 && (
          <div style={{marginTop:14,padding:"11px 13px",borderRadius:10,background:"var(--surface2)",border:"1px solid var(--border)"}}>
            <div style={{fontSize:11.5,fontWeight:700,color:"var(--red-text)",marginBottom:6}}>
              {prev.errores.length} línea{prev.errores.length===1?"":"s"} se ignorarán
            </div>
            <div style={{maxHeight:110,overflowY:"auto"}}>
              {prev.errores.slice(0,15).map((e,i) => (
                <div key={i} style={{fontSize:10.5,color:"var(--text3)",lineHeight:1.5}}>
                  <b>L{e.linea}</b> · {e.motivo} — <span style={{fontFamily:"var(--mono)"}}>{e.texto}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Vista previa ── */}
        {listo && prev.vista_previa?.length > 0 && (
          <div style={{marginTop:14,padding:"11px 13px",borderRadius:10,background:"var(--surface2)",border:"1px solid var(--border)"}}>
            <div style={{...lbS,marginBottom:7}}>Se va a importar</div>
            <div style={{maxHeight:150,overflowY:"auto"}}>
              {prev.vista_previa.map((v,i) => (
                <div key={i} style={{display:"flex",gap:8,fontSize:11,color:"var(--text2)",padding:"2px 0"}}>
                  <span style={{width:150,flexShrink:0,fontFamily:"var(--mono)",fontSize:10.5}}>
                    {fmt(v.semana_inicio || v.fecha)}{v.fecha_fin ? ` – ${fmt(v.fecha_fin)}` : ""}
                  </span>
                  <span style={{flex:1,fontWeight:600,color:"var(--text)"}}>{v.nombre}</span>
                  {v.tipo && v.tipo !== "vacaciones" && <span style={{color:"var(--text3)"}}>{v.tipo}</span>}
                </div>
              ))}
            </div>
          </div>
        )}

        <div style={{display:"flex",gap:8,justifyContent:"flex-end",marginTop:16}}>
          <button className="btn" onClick={onClose}
            style={{padding:"8px 16px",borderRadius:9,border:"1px solid var(--border)",cursor:"pointer",fontSize:13,background:"var(--surface2)",color:"var(--text2)",fontFamily:"var(--font)"}}>
            Cancelar
          </button>
          <BtnPrimary onClick={confirmar} disabled={!listo || busy}>
            {busy && prev ? "Importando…" : "Importar"}
          </BtnPrimary>
        </div>
      </div>
    </div>
  );
}
